import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import Select from "react-select";
import axiosInstance from "../../../axiosInterceptor";
import Breadcrumb from "../../../helper/Breadcrumb";
import Loader from "../../../Components/Loader";
import { MenuSchema } from "../../../Schema/Schema";
import { SwitchToggle } from "../../../helper/ImageFormat";
import { fetchAllData } from "../../../helper/ProductAttributeHandling";
import { selectStyles } from "../../../utils/selectStyles";

const AddSubMenu = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const editData = location.state;
    const [loading, setLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [categoryData, setCategoryData] = useState([]);
    const [cmsData, setCmsData] = useState([]);
    const initialValues = {
        label: "",
        type: "",
        url: "",
        isMega: false,
        categoryId: null,
        cmsId: null,
        parentId: null,
        order: editData?.position || 1,
        meta_title: "",
        meta_keyword: "",
        meta_description: "",
        isActive: true,
        showInHeader: false,
        showInFooter: false,
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([
                fetchAllData("/category", setCategoryData),
                fetchAllData("/cms", setCmsData),
            ]);
            setLoading(false);
        };
        fetchData();
    }, []);

    const formik = useFormik({
        initialValues,
        validationSchema: MenuSchema,
        onSubmit: async (values) => {
            setBtnLoading(true);
            try {
                const payload = {
                    label: values.label,
                    type: values.type,
                    url: values.url,
                    isMega: values.isMega,
                    categoryId: values.type === "CATEGORY" ? values.categoryId : null,
                    cmsId: values.type === "CMS" ? values.cmsId : null,
                    parentId: values.parentId || null,
                    order: values.order || 0,
                    meta_title: values.meta_title,
                    meta_keyword: values.meta_keyword,
                    meta_description: values.meta_description,
                    isActive: values.isActive,
                    showInHeader: values.showInHeader,
                    showInFooter: values.showInFooter,
                };

                const response = editData?.id
                    ? await axiosInstance.put(`/menu/${editData.id}`, payload)
                    : await axiosInstance.post("/menu", payload);

                if (response.status === 200) {
                    toast.success(response.data.message);
                    navigate(-1);
                }
            } catch (err) {
                toast.error(err.response?.data?.message || "Something went wrong!");
            } finally {
                setBtnLoading(false);
            }
        },
    });

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue, handleBlur } = formik;

    useEffect(() => {
        if (editData?.id) {
            Object?.keys(initialValues)?.forEach((key) => {
                setFieldValue(key, editData[key] ?? initialValues[key]);
            });
        }
        if (editData?.parentId) {
            setFieldValue("parentId", editData?.parentId);
        }
    }, [editData]);

    if (loading) return <Loader />;

    const breadcrumbdata = [
        { label: "Home", path: "/" },
        { label: editData?.id ? "Update Menu" : "Add Menu", path: "/add-menu" },
    ];

    const menuTypeOptions = [
        { label: "Category", value: "CATEGORY" },
        { label: "Brand", value: "BRAND" },
        { label: "Cms", value: "CMS" },
        { label: "Static", value: "STATIC" },
    ];

    const categoryOptions = categoryData.flatMap((parent) =>
        parent.children?.length
            ? [
                { value: parent.id, label: `${parent.name} (Parent)` },
                ...parent.children.map((child) => ({
                    value: child.id,
                    label: `-- ${child.name}`,
                })),
            ]
            : [{ value: parent.id, label: parent.name }]
    );


    const cmsOptions = cmsData.map((c) => ({
        value: c.id,
        label: c.name || c.title || `CMS ${c.id}`,
    }));


    return (
        <div className="content-wrapper">
            <div className="page-header mt-4">
                <h3 className="page-title">{editData?.id ? "Update" : "Add"} Menu</h3>
                <Breadcrumb items={breadcrumbdata} />
            </div>

            <div className="row grid-margin stretch-card">
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-body">
                            <form onSubmit={handleSubmit} className="pt-0">
                                <div className="row g-3">
                                <div className="col-sm-4">
                                        <label className="col-form-label">Sort Order</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm border border-dark"
                                            name="order"
                                            value={values.order}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </div>
                                    
                                    <div className="col-sm-4">
                                        <label className="col-form-label">Menu Type</label>
                                        <Select
                                            options={menuTypeOptions}
                                             styles={selectStyles}
                                            value={menuTypeOptions.find((opt) => opt.value === values.type) || null}
                                            onChange={(option) => {
                                                setFieldValue("type", option?.value || "");
                                                setFieldValue("categoryId", null);
                                                setFieldValue("brandId", null);
                                                setFieldValue("cmsId", null);
                                                setFieldValue("attribute_id", null);
                                                setFieldValue("attributeChild_id", null);
                                            }}
                                            isClearable
                                            placeholder="Select Menu Type"
                                        />
                                        {touched.type && errors.type && <div className="text-danger">{errors.type}</div>}
                                    </div>
                                    {values.type === "CATEGORY" && (
                                        <div className="col-sm-4">
                                            <label className="col-form-label">Category</label>
                                            <Select
                                                 styles={selectStyles}
                                                options={categoryOptions}
                                                value={categoryOptions.find((opt) => opt.value === values.categoryId) || null}
                                                onChange={(option) => setFieldValue("categoryId", option?.value || null)}
                                                isClearable
                                                placeholder="Select Category"
                                            />
                                        </div>
                                    )}

                                   

                                    {values.type === "CMS" && (
                                        <div className="col-sm-4">
                                            <label className="col-form-label">CMS</label>
                                            <Select
                                                styles={selectStyles}
                                                options={cmsOptions}
                                                value={cmsOptions.find((opt) => opt.value === values.cmsId) || null}
                                                onChange={(option) => setFieldValue("cmsId", option?.value || null)}
                                                isClearable
                                                placeholder="Select CMS"
                                            />
                                        </div>
                                    )}

                                   

                                    <div className="col-sm-4">
                                        <label className="col-form-label">Title</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm border border-dark"
                                            name="label"
                                            value={values.label}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter Title"
                                        />
                                        {touched.label && errors.label && <div className="text-danger">{errors.label}</div>}
                                    </div>

                                    <div className="col-sm-4">
                                        <label className="col-form-label">URL</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm border border-dark"
                                            name="url"
                                            value={values.url}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter URL"
                                        />
                                    </div>

                                    

                                    <div className="col-sm-4">
                                        <label className="col-form-label">Meta Title</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm border border-dark"
                                            name="meta_title"
                                            value={values.meta_title}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter meta title"
                                        />
                                    </div>

                                    <div className="col-sm-4">
                                        <label className="col-form-label">Meta Keyword</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm border border-dark"
                                            name="meta_keyword"
                                            value={values.meta_keyword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter meta keyword"
                                        />
                                    </div>

                                    <div className="col-sm-12">
                                        <label className="col-form-label">Meta Description</label>
                                        <textarea
                                            className="form-control form-control-sm border border-dark"
                                            name="meta_description"
                                            value={values.meta_description}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter meta description"
                                            rows={4}
                                        />
                                    </div>

                                    <div className="col-md-12 mt-4">
                                        <SwitchToggle
                                            checked={values.isActive}
                                            onChange={(val) => setFieldValue("isActive", val)}
                                            activeLabel="Active"
                                            inactiveLabel="Inactive"
                                        />
                                    </div>

                                    <div className="col-md-3 d-flex align-items-center gap-2 mt-4">
                                        <label htmlFor="showInHeader" className="form-label mb-0 me-2" style={{ width: "140px" }}>
                                            Show In Header
                                        </label>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                id="showInHeader"
                                                name="showInHeader"
                                                checked={values.showInHeader}
                                                onChange={() => setFieldValue("showInHeader", !values.showInHeader)}
                                            />
                                            <label htmlFor="showInHeader" className="switch-label">
                                                <span className="switch-inner" />
                                                <span className="switch-handle" />
                                            </label>
                                        </div>
                                    </div>

                                    <div className="col-md-3 d-flex align-items-center gap-2 mt-4">
                                        <label htmlFor="showInFooter" className="form-label mb-0 me-2" style={{ width: "140px" }}>
                                            Show In Footer
                                        </label>
                                        <div className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                id="showInFooter"
                                                name="showInFooter"
                                                checked={values.showInFooter}
                                                onChange={() => setFieldValue("showInFooter", !values.showInFooter)}
                                            />
                                            <label htmlFor="showInFooter" className="switch-label">
                                                <span className="switch-inner" />
                                                <span className="switch-handle" />
                                            </label>
                                        </div>
                                    </div>
                                    
                                    <div className="col-12 mt-4 d-flex justify-content-center " style={{ gap: "15px" }}>
                                        <button type="submit" className="btn btn-primary" disabled={btnLoading}>
                                            {btnLoading ? "Loading..." : editData?.id ? "Update" : "Add"}
                                        </button>
                                        <Link to="/menu">
                                            <button type="button" className="btn btn-danger">
                                                Cancel
                                            </button>
                                        </Link>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddSubMenu;
